import { useState, useMemo, useEffect, forwardRef, useImperativeHandle } from 'react';
import { Trash2, List, LayoutList, Plus, Pencil, X, AlertCircle } from 'lucide-react';
import { ColumnDef } from '@tanstack/react-table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/useToast';
import type { CampaignData, StructuredSnippetType } from '@/lib/types';
import { useUpdateStructuredSnippetMutationOptions, useDeleteStructuredSnippetMutationOptions } from '@/lib/query-options';
import { useRefetchCampaign } from '@/hooks/useRefetchCampaign';
import { ConformationModal } from '@/components/glitch-ui/confirmation-modal';
import { DataTable } from '@/components/glitch-ui/data-table';
import { IconButton } from '@/components/glitch-ui/icon-button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { FieldError, InputAdornment } from '@/components/glitch-ui/field-error';

const HEADER_TYPES = [
  'amenities',
  'brands',
  'courses',
  'degree programs',
  'destinations',
  'featured hotels',
  'insurance coverage',
  'models',
  'neighborhoods',
  'services (service catalog)',
  'shows',
  'styles',
  'types',
] as const;

function toLabel(headerType: string) {
  return headerType.charAt(0).toUpperCase() + headerType.slice(1);
}

// #5851: case- and whitespace-insensitive key for duplicate-header detection.
const normalizeHeaderType = (headerType: string) => headerType.toLowerCase().replace(/\s+/g, ' ').trim();

interface StructuredSnippetSectionProps {
  campaign: CampaignData;
  disabled?: boolean;
}

const EMPTY_SNIPPET: Omit<StructuredSnippetType, 'id'> = {
  header_type: HEADER_TYPES[0],
  values: [{ value: '' }, { value: '' }, { value: '' }],
};

export interface StructuredSnippetSectionRef {
  openAdd: () => void;
}

export const StructuredSnippetSection = forwardRef<StructuredSnippetSectionRef, StructuredSnippetSectionProps>(({ campaign, disabled = false }, ref) => {
  const { id: campaignId } = campaign;
  const refetchCampaign = useRefetchCampaign(campaign.slug);
  const { toast } = useToast();

  const [localSnippets, setLocalSnippets] = useState<StructuredSnippetType[]>(campaign.structured_snippets || []);
  // Sync from campaign when it changes externally (e.g. after add/delete). Previously this used
  // an in-render `useRef` check keyed on `JSON.stringify(...map(s => s.id))`, which (a) missed
  // content-only changes and (b) didn't reliably commit under React 18 strict-mode double-render,
  // so adds/deletes required a manual refresh to show (#4457). React Query's structuralSharing
  // preserves array reference equality when contents are unchanged, so a plain reference-keyed
  // effect is enough.
  useEffect(() => {
    setLocalSnippets(campaign.structured_snippets || []);
  }, [campaign.structured_snippets]);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingSnippet, setEditingSnippet] = useState<StructuredSnippetType | null>(null);
  const [modalHeaderType, setModalHeaderType] = useState<string>(EMPTY_SNIPPET.header_type);
  const [modalValues, setModalValues] = useState<string[]>(['', '', '']);
  const [modalError, setModalError] = useState('');
  const [deletingSnippet, setDeletingSnippet] = useState<StructuredSnippetType | null>(null);
  // Tell-on-click (#5857): Save stays enabled; clicking with <3 values marks the
  // empty inputs and puts the reason on the label row.
  const [valuesError, setValuesError] = useState('');

  const updateMutation = useUpdateStructuredSnippetMutationOptions();
  const deleteMutation = useDeleteStructuredSnippetMutationOptions();

  const openAddModal = () => {
    setEditingSnippet(null);
    setModalHeaderType(EMPTY_SNIPPET.header_type);
    setModalValues(['', '', '']);
    setValuesError('');
    setModalError('');
    setIsModalOpen(true);
  };

  const openEditModal = (snippet: StructuredSnippetType) => {
    setEditingSnippet(snippet);
    setModalHeaderType(snippet.header_type);
    setModalValues(snippet.values.map(v => v.value));
    setValuesError('');
    setModalError('');
    setIsModalOpen(true);
  };

  const handleAddValue = () => {
    if (modalValues.length >= 10) return;
    setModalValues(prev => [...prev, '']);
  };

  const handleRemoveValue = (index: number) => {
    if (modalValues.length <= 3) return;
    setModalValues(prev => prev.filter((_, i) => i !== index));
  };

  const handleValueChange = (index: number, value: string) => {
    setModalValues(prev => prev.map((v, i) => (i === index ? value : v)));
    setValuesError('');
  };

  const isValid = modalValues.filter(v => v.trim()).length >= 3;

  const handleSave = async () => {
    // Tell-on-click (#5857): mark the values field when fewer than 3 are filled.
    if (!isValid) {
      setValuesError('Fill in at least 3 values');
      return;
    }

    // #5851: block a second snippet whose header already exists on this campaign, which would create a
    // redundant Google Ads header asset. Exclude the snippet being edited.
    const headerKey = normalizeHeaderType(modalHeaderType);
    const duplicateHeader = localSnippets
      .filter((s) => !editingSnippet || s.id !== editingSnippet.id)
      .some((s) => normalizeHeaderType(s.header_type) === headerKey);
    if (duplicateHeader) {
      setModalError(`A "${toLabel(modalHeaderType)}" snippet already exists on this campaign. Edit it to add more values.`);
      return;
    }

    const snippet: StructuredSnippetType = {
      ...(editingSnippet?.id ? { id: editingSnippet.id } : {}),
      header_type: modalHeaderType,
      values: modalValues.filter(v => v.trim()).map(v => ({ value: v.trim() })),
    };
    try {
      await updateMutation.mutateAsync({ campaignId, structuredSnippet: snippet });
      setIsModalOpen(false);
      refetchCampaign();
    } catch (error) {
      console.error(error);
      toast({
        description: error instanceof Error ? error.message : 'Failed to save structured snippet',
        variant: 'destructive',
      });
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deletingSnippet?.id) return;
    try {
      await deleteMutation.mutateAsync({ campaignId, structuredSnippetId: deletingSnippet.id });
      setDeletingSnippet(null);
      refetchCampaign();
    } catch (error) {
      console.error(error);
      toast({
        description: error instanceof Error ? error.message : 'Failed to delete structured snippet',
        variant: 'destructive',
      });
    }
  };

  const columns: ColumnDef<StructuredSnippetType, unknown>[] = useMemo(() => [
    {
      accessorKey: 'header_type',
      header: 'Header',
      meta: { className: 'px-6 w-[25%]' },
      cell: ({ row }) => (
        <div className='flex items-center gap-2'>
          <List className='h-3.5 w-3.5 text-gray-400 shrink-0' />
          <span className='text-sm text-gray-900 font-medium'>{toLabel(row.original.header_type)}</span>
        </div>
      ),
    },
    {
      accessorKey: 'values',
      header: 'Values',
      cell: ({ row }) => (
        <div className='flex flex-wrap gap-1'>
          {row.original.values.map((v, i) => (
            <span key={i} className='inline-flex items-center px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-700'>
              {v.value}
            </span>
          ))}
        </div>
      ),
    },
    {
      id: 'actions',
      header: () => null,
      enableSorting: false,
      cell: ({ row }) => {
        if (disabled) return null;
        return (
          <div className='flex justify-end gap-1'>
            <IconButton variant='default' onClick={() => openEditModal(row.original)}>
              <Pencil className='h-4 w-4' />
            </IconButton>
            <IconButton variant='destructive' onClick={() => setDeletingSnippet(row.original)}>
              <Trash2 className='h-4 w-4' />
            </IconButton>
          </div>
        );
      },
    },
  ], [disabled]);

  useImperativeHandle(ref, () => ({ openAdd: openAddModal }), [openAddModal]);

  return (
    <div>

      <DataTable<StructuredSnippetType>
        data={localSnippets}
        columns={columns}
        variant='bordered'
        pageSize={999}
        emptyIcon={LayoutList}
        emptyTitle='No Structured Snippets'
        emptyDescription='Add structured snippets to highlight specific aspects of your products or services.'
      />

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className='sm:max-w-[480px] max-h-[90vh] overflow-y-auto' aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle>{editingSnippet ? 'Edit Snippet' : 'Add Structured Snippet'}</DialogTitle>
          </DialogHeader>
          <div className='space-y-4 py-2'>
            <div className='space-y-2'>
              <Label>Header type</Label>
              <Select
                value={modalHeaderType}
                onValueChange={(v) => {
                  setModalHeaderType(v);
                  setModalError('');
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder='Select header type' />
                </SelectTrigger>
                <SelectContent>
                  {HEADER_TYPES.map(type => (
                    <SelectItem key={type} value={type}>
                      {toLabel(type)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {modalError && (
                <div className='flex items-start gap-2 px-3 py-2 rounded-lg border-l-4 bg-[#FEE2E2] border-[#FCA5A5] text-[#B91C1C] text-sm'>
                  <AlertCircle className='h-4 w-4 shrink-0 mt-0.5' />
                  <span>{modalError}</span>
                </div>
              )}
            </div>

            <div className='space-y-2'>
              <div className='flex items-center justify-between'>
                <Label>Values <span className='text-gray-400 font-normal'>(min 3, max 10)</span></Label>
                <FieldError message={valuesError} />
              </div>
              <div className='space-y-2'>
                {modalValues.map((val, index) => (
                  <div key={index} className='flex items-center gap-2'>
                    <div className='relative flex-1'>
                      <Input
                        value={val}
                        onChange={e => handleValueChange(index, e.target.value)}
                        placeholder={`Value ${index + 1}`}
                        maxLength={25}
                        error={!!valuesError && !val.trim()}
                        className={valuesError && !val.trim() ? 'pr-8' : ''}
                      />
                      <InputAdornment error={!!valuesError && !val.trim()} />
                    </div>
                    {modalValues.length > 3 && (
                      <button
                        onClick={() => handleRemoveValue(index)}
                        className='p-1 text-gray-400 hover:text-red-600 rounded transition-colors shrink-0'
                      >
                        <X className='h-4 w-4' />
                      </button>
                    )}
                  </div>
                ))}
              </div>
              {modalValues.length < 10 && (
                <button
                  onClick={handleAddValue}
                  className='text-sm text-[#290BD9] hover:text-[#2309C5] flex items-center gap-1 mt-1'
                >
                  <Plus className='h-3.5 w-3.5' />
                  Add value
                </button>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant='outline' onClick={() => setIsModalOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={updateMutation.isPending}
              className='bg-[#290BD9] hover:bg-[#2309C5] leading-none'
            >
              {updateMutation.isPending ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConformationModal
        isOpen={!!deletingSnippet}
        onClose={() => setDeletingSnippet(null)}
        onConfirm={handleDeleteConfirm}
        title='Delete Structured Snippet'
        description={`Are you sure you want to delete this "${deletingSnippet ? toLabel(deletingSnippet.header_type) : ''}" snippet? This action cannot be undone.`}
        confirmText='Delete'
        cancelText='Cancel'
        isLoading={deleteMutation.isPending}
      />
    </div>
  );
});
StructuredSnippetSection.displayName = 'StructuredSnippetSection';
